import { Injectable, signal } from '@angular/core';
import {
  AddRelatedPartResponse,
  createAddRelatedPartPayload,
} from '../../job-detail/live-code-scanner/related-part-registration';
import { SocketioService } from '../SocketService/socketio.service';
import { JobConditionOption, normalizeJobConditionOptions } from '../../shared/job-condition';
import type { DeliveryCompletionMethod, DeliveryPreparationForm } from '../../delivery-preparation/delivery-preparation';

export interface JobListReturnState {
  title: string;
  searchQuery: string;
  scrollTop?: number;
  loadedCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private readonly jobDetail = signal<any>(undefined);
  private readonly jobListReturnState = signal<JobListReturnState | undefined>(undefined);
  private readonly errorLog = signal<readonly string[]>([]);
  private jobConditionOptions?: readonly JobConditionOption[];
  private jobConditionRequest?: Promise<readonly JobConditionOption[]>;

  constructor(private socket: SocketioService) { }

  addRelatedPart(jobId: number, identifiers: readonly string[]): Promise<AddRelatedPartResponse> {
    return this.socket.emitEvent(
      'addRelatedPart',
      createAddRelatedPartPayload(jobId, identifiers),
    );
  }

  completeJobWork(jobId: number): Promise<any> {
    return this.socket.emitEvent('completeJobWork', { jobId });
  }

  completeJobDiagnosis(jobId: number): Promise<any> {
    return this.socket.emitEvent('completeJobDiagnosis', { jobId });
  }

  acceptJobEstimate(jobId: number): Promise<any> {
    return this.socket.emitEvent('acceptJobEstimate', { jobId });
  }

  returnJobAsIs(jobId: number, reason: string): Promise<any> {
    return this.socket.emitEvent('returnJobAsIs', { jobId, reason });
  }

  getDeliveryPreparation(jobId: number): Promise<any> {
    // 読み取り専用要求なので、Socket ackが失われた場合も画面を永久ローディングにしない。
    return this.socket.emitEventWithTimeout('getDeliveryPreparation', { jobId }, 10000);
  }

  saveDeliveryPreparation(jobId: number, delivery: DeliveryPreparationForm): Promise<any> {
    // 保存ACKが失われても、納品準備画面を操作不能のままにしない。
    return this.socket.emitEventWithTimeout('saveDeliveryPreparation', { jobId, delivery }, 20000);
  }

  getSquarePaymentStatus(jobId: number): Promise<any> {
    // 旧Square条件の照合では、Invoice一覧と注文を安全に読み取るため最大15秒を使う。
    // ACK不達でも20秒で画面を復帰させる。
    return this.socket.emitEventWithTimeout('getSquarePaymentStatus', { jobId }, 20000);
  }

  getSquarePaymentsByAmount(amount: number): Promise<any> {
    // 直近30日を全ページ取得する読取りAPI。ACK不達時も呼出元を無期限に待たせない。
    return this.socket.emitEventWithTimeout('getSquarePaymentsByAmount', { amount }, 60000);
  }

  getRecentSquareInvoices(limit = 20): Promise<any> {
    // SquareのInvoice情報を省略せず返す読み取りAPI。ACK不達時も画面を待たせ続けない。
    return this.socket.emitEventWithTimeout('getRecentSquareInvoices', { limit }, 20000);
  }

  getExternalSquareInvoiceStatus(jobId: number, amount: number): Promise<any> {
    // Square管理画面で発行された直近請求書を、JOBコードと入力金額で読取り照合する。
    return this.socket.emitEventWithTimeout('getExternalSquareInvoiceStatus', { jobId, amount }, 30000);
  }

  cancelSquareInvoice(invoiceId: string): Promise<any> {
    // 取消は自動再送しない。結果不明時は呼出側でInvoiceを再取得して照合する。
    return this.socket.emitEventWithTimeout('cancelSquareInvoice', { invoiceId }, 20000);
  }

  resetExternalSquarePayment(jobId: number): Promise<any> {
    // 一覧の全ページ照合、状態別取消、関連Order確認後のDB初期化をサーバー側で一続きに行う。
    // 外部処理のためタイムアウト後に自動再送せず、利用者の明示操作で再照合する。
    return this.socket.emitEventWithTimeout('resetExternalSquarePayment', { jobId }, 90000);
  }

  getSquareInvoiceDraft(jobId: number): Promise<any> {
    return this.socket.emitEventWithTimeout('getSquareInvoiceDraft', { jobId }, 15000);
  }

  discardSquareInvoiceDraft(jobId: number): Promise<any> {
    return this.socket.emitEventWithTimeout('discardSquareInvoiceDraft', { jobId }, 45000);
  }

  cancelSquareBillingReservation(jobId: number): Promise<any> {
    // ローカル予約だけを取り消す。Square APIへは一切書き込まない。
    return this.socket.emitEventWithTimeout('cancelSquareBillingReservation', { jobId }, 20000);
  }

  resetSquareOrderOnly(jobId: number): Promise<any> {
    // 一時DRAFTの作成・削除とOrder取消確認を含むため、ACK不達時にも画面を復帰できる上限を明示する。
    return this.socket.emitEventWithTimeout('resetSquareOrderOnly', { jobId }, 60000);
  }

  reconcileSquareInvoiceDiscard(jobId: number): Promise<any> {
    return this.socket.emitEventWithTimeout('reconcileSquareInvoiceDiscard', { jobId }, 30000);
  }

  completeDeliveryPreparation(jobId: number): Promise<any> {
    return this.socket.emitEvent('completeDeliveryPreparation', { jobId });
  }

  completeJobDelivery(jobId: number, completionMethod: DeliveryCompletionMethod, completionDate: string): Promise<any> {
    // 状態更新のACK不達時は結果不明とし、この呼出し自体は自動再送しない。
    return this.socket.emitEventWithTimeout(
      'completeJobDelivery',
      { jobId, completionMethod, completionDate },
      20_000,
    );
  }

  returnDeliveryToPreparation(jobId: number): Promise<any> {
    return this.socket.emitEvent('returnDeliveryToPreparation', { jobId });
  }

  getJobConditionOptions(): Promise<readonly JobConditionOption[]> {
    if (this.jobConditionOptions) return Promise.resolve(this.jobConditionOptions);
    if (this.jobConditionRequest) return this.jobConditionRequest;

    this.jobConditionRequest = this.socket.emitEvent('getJobCondition', {})
      .then(response => {
        if (response?.status !== 'success') {
          throw new Error(response?.detail ?? '状態マスターを取得できませんでした。');
        }
        const options = normalizeJobConditionOptions(response.data);
        this.jobConditionOptions = options;
        return options;
      })
      .finally(() => {
        this.jobConditionRequest = undefined;
      });
    return this.jobConditionRequest;
  }

  setJobDetail( jobDetail: any) {
    this.jobDetail.set(jobDetail);
  }

  getJobDetail() {
    return this.jobDetail();
  }

  setJobListReturnState(state: JobListReturnState): void {
    this.jobListReturnState.set(state);
  }

  consumeJobListReturnState(): JobListReturnState | undefined {
    const state = this.jobListReturnState();
    this.jobListReturnState.set(undefined);
    return state;
  }
  
  setErrorLog(errorLog: string) {
    this.errorLog.update(currentLogs => [...currentLogs, errorLog]);
  }
  
  getErrorLog() {
    return this.errorLog();
  }
  
  clearErrorLog() {
    this.errorLog.set([]);
  }
}
